import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axiosInstance from '../../services/axiosInstance';
import { fetchUserData } from './user.slice';
import { clearAuthStorage } from '../../utils/authStorage';

const initialState = {
    user: null,
    isAuthenticated: false,
    loading: false,
    error: null,
    loggedIn: false,
    isLoggedOut: false,
    message: null,
    resetToken: null
};

export const login = createAsyncThunk(
    'auth/login',
    async (data, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.post('/login', data);
            const { user, message } = response.data;

            dispatch(fetchUserData(user._id));

            return { user, message };
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Invalid email or password" });
        }
    }
);

export const register = createAsyncThunk(
    'auth/register',
    async (userData, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.post('/register', userData);
            const { user, message, email } = response.data;

            if (user) {
                dispatch(fetchUserData(user._id));
            }

            return { user, message, email };
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Registration failed" });
        }
    }
);

export const logout = createAsyncThunk(
    'auth/logout',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post('/logout');

            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Logout failed" });
        }
    }
);

export const forgotPassword = createAsyncThunk(
    'auth/forgotPassword',
    async (email, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post('/forgot-password', { email });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Forgot Password request failed" });
        }
    }
);

export const verifyOtp = createAsyncThunk(
    'auth/verifyOtp',
    async ({ email, otp }, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.post('/verify', { email, otp });
            const { user, isVerified } = response.data;
            if (user && isVerified) {
                dispatch(fetchUserData(user._id));
            }
            return response.data; // Now returns resetToken or user
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Verify OTP failed" });
        }
    }
);

export const resetPassword = createAsyncThunk(
    'auth/resetPassword',
    async ({ email, newPassword, resetToken }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post('/change-password', { email, newPassword, resetToken });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Reset Password failed" });
        }
    }
);

export const updateUserProfile = createAsyncThunk(
    'auth/updateUserProfile',
    async ({ userId, email, firstName, lastName, oldPassword, newPassword, photo, selectedFile, status, newsletterEnabled }, { rejectWithValue, dispatch }) => {
        try {
            if (newPassword) {
                await axiosInstance.post('/change-password', {
                    email,
                    oldPassword,
                    newPassword
                });
            }

            let response;
            if (selectedFile) {
                const formData = new FormData();
                formData.append('firstName', firstName);
                formData.append('lastName', lastName);
                formData.append('email', email);
                formData.append('photo', selectedFile);
                if (status) {
                    formData.append('status', status);
                }
                if (typeof newsletterEnabled !== 'undefined') {
                    formData.append('newsletterEnabled', newsletterEnabled);
                }

                response = await axiosInstance.put(`/users/${userId}`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            } else {
                response = await axiosInstance.put(`/users/${userId}`, {
                    firstName,
                    lastName,
                    email,
                    photo,
                    status,
                    newsletterEnabled
                });
            }

            if (response.data.success) {
                dispatch(updateUserSuccess(response.data.user));
                return response.data.user;
            } else {
                return rejectWithValue(response.data.message || "Failed to update profile");
            }
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Internal server error saving settings");
        }
    }
);

export const deleteAccount = createAsyncThunk(
    'auth/deleteAccount',
    async (userId, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.delete(`/users/${userId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message || "Account deletion failed");
        }
    }
);

export const toggleNewsletter = createAsyncThunk(
    'auth/toggleNewsletter',
    async (isSubscribed, { rejectWithValue, dispatch }) => {
        try {
            const response = await axiosInstance.put('/newsletter/toggle', { isSubscribed });
            if (response.data.success) {
                dispatch(updateUserSuccess(response.data.user));
                return response.data.user;
            }
            return rejectWithValue(response.data.message || "Failed to update subscription status");
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message || "Request failed");
        }
    }
);

export const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        logoutUser: (state) => {
            state.user = null;
            state.isAuthenticated = false;
            state.loggedIn = false;
            state.isLoggedOut = true;
            state.error = null;
            state.message = null;
            clearAuthStorage();
        },
        updateUserSuccess: (state, action) => {
            state.user = action.payload;
        },
        rehydrateAuth: (state, action) => {
            const user = action.payload;
            state.user = user;
            state.isAuthenticated = !!user;
        },
        clearAuthError: (state) => {
            state.error = null;
            state.message = null;
        }
    },

    extraReducers: (builder) => {
        builder
            .addCase(login.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.message = null;
            })
            .addCase(login.fulfilled, (state, action) => {
                state.user = action.payload?.user || null;
                state.isAuthenticated = true;
                state.loading = false;
                state.error = null;
                state.message = action.payload?.message || "Login successfully";
            })
            .addCase(login.rejected, (state, action) => {
                state.loading = false;
                state.isAuthenticated = false;
                state.error = action.payload?.message || "Login Failed";
                state.message = action.payload?.message || "Login Failed";
            })
            .addCase(register.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.message = null;
            })
            .addCase(register.fulfilled, (state, action) => {
                state.user = action.payload?.user || null;
                state.isAuthenticated = !!action.payload?.user;
                state.loading = false;
                state.error = null;
                state.message = action.payload?.message || "Register successfully";
            })
            .addCase(register.rejected, (state, action) => {
                state.loading = false;
                state.isAuthenticated = false;
                state.error = action.payload?.message || "Registration Failed";
                state.message = action.payload?.message || "Registration Failed";
            })
            .addCase(forgotPassword.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.message = null;
            })
            .addCase(forgotPassword.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.message = action.payload?.message || "OTP Sent Successfully...";
            })
            .addCase(forgotPassword.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Forgot Password Failed";
                state.message = action.payload?.message || "Forgot Password Failed";
            })
            .addCase(verifyOtp.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.message = null;
            })
            .addCase(verifyOtp.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.message = action.payload?.message || "Otp Verify SuccessFully...";
                state.resetToken = action.payload?.resetToken || null;
                if (action.payload?.user && action.payload?.isVerified) {
                    state.user = action.payload.user;
                    state.isAuthenticated = true;
                }
            })
            .addCase(verifyOtp.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Verify OTP Failed";
                state.message = action.payload?.message || "Verify OTP Failed";
            })
            .addCase(resetPassword.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.message = null;
            })
            .addCase(resetPassword.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.message = action.payload?.message || "Password Changed SuccessFully...";
            })
            .addCase(resetPassword.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Reset Password Failed";
                state.message = action.payload?.message || "Reset Password Failed";
            })
            .addCase(updateUserProfile.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateUserProfile.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload;
                state.error = null;
            })
            .addCase(updateUserProfile.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(logout.fulfilled, (state) => {
                state.user = null;
                state.isAuthenticated = false;
                state.isLoggedOut = true;
                clearAuthStorage();
            })
            .addCase(deleteAccount.fulfilled, (state) => {
                state.user = null;
                state.isAuthenticated = false;
                state.isLoggedOut = true;
                clearAuthStorage();
            })
            .addCase(toggleNewsletter.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(toggleNewsletter.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload;
                state.error = null;
            })
            .addCase(toggleNewsletter.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    }
});

export const { logoutUser, updateUserSuccess, rehydrateAuth, clearAuthError } = authSlice.actions;
export default authSlice.reducer;
